/** NOTES ON A CALL (081): internal, and for the responder.
 *
 *  The customer: "one should be internal notes and one should be notes that
 *  will be sent to the responder taking the call. And it should be a big
 *  difference to those 2 boxes ... visible for the eye", with room for
 *  "thousands of characters ... sometimes people, they pick up the phone and
 *  they just start talking". Shown on `call-notes-mockup.html`.
 *
 *  INTERNAL IS GREY, DASHED, WITH A LOCK, "NEVER SENT"; FOR THE RESPONDER IS
 *  GREEN, SOLID, WITH A SEND ARROW, "SENT TO HIM". The two must never be
 *  mistaken for each other -- that is the whole point of having two.
 *
 *  ON TAKE A CALL each is one box, in step 1 and again under the nature in
 *  step 3 -- the same text, typed in either place (`NotePair`). ON THE OPEN
 *  CALL more is ADDED, each addition with who and when, never written over
 *  (his Q1 A, `CallNotes`); Past calls shows them the same way, read-only. */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { ApiError, api } from '../lib/api'
import type { CallNote, NoteSent } from '../lib/api'
import { hhmm } from './CallParts'

/** The server keeps this much of each note; the box stops here first. */
export const NOTE_MAX = 20000
export type NoteKind = 'internal' | 'responder'

const WORDS: Record<NoteKind, { title: string; tag: string; sub: string; short: string }> = {
  internal: {
    title: 'Internal notes', tag: 'Never sent',
    sub: 'Only in the CRM, for dispatch. Never in a text, an email or the broadcast.',
    short: 'Only in the CRM, for dispatch.',
  },
  responder: {
    title: 'Notes for the responder', tag: 'Sent to him',
    sub: 'Sent to the member who takes the call, with the full address.',
    short: 'Sent to the member who takes the call.',
  },
}

function Icon({ kind }: { kind: NoteKind }) {
  return kind === 'internal' ? (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M1.5 8 14.5 2 11 14.5 7.5 9.5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M7.5 9.5 14.5 2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

function Head({ kind, short }: { kind: NoteKind; short?: boolean }) {
  return (
    <>
      <div className="cn-hd"><Icon kind={kind} />{WORDS[kind].title}<span className="cn-tag">{WORDS[kind].tag}</span></div>
      <div className="cn-sub">{short ? WORDS[kind].short : WORDS[kind].sub}</div>
    </>
  )
}

/** A BOX THAT GROWS WITH WHAT IS TYPED, so a caller's whole story is on
 *  screen rather than behind a scroll bar inside a scroll bar. */
export function GrowingText(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fit = () => {
    const el = ref.current
    // A HIDDEN BOX MEASURES NOTHING: step 3's copy is hidden while step 1 is
    // typed in, and is fitted when it is shown (the observer below).
    if (!el || el.offsetParent === null) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }
  useLayoutEffect(fit, [props.value])
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    let shown = el.offsetParent !== null
    const watch = new ResizeObserver(() => {
      const now = el.offsetParent !== null
      if (now && !shown) fit()
      shown = now
    })
    watch.observe(el)
    return () => watch.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <textarea ref={ref} rows={4} {...props} />
}

/** Nothing counts down until it is close (the mock-up's promise). */
function Room({ length }: { length: number }) {
  return length > NOTE_MAX - 2000
    ? <div className="cn-room">{(NOTE_MAX - length).toLocaleString()} characters left</div>
    : null
}

/** THE TWO BOXES ON TAKE A CALL: the same two texts wherever they are drawn,
 *  so step 3 carries on what step 1 started. */
export function NotePair({ internal, responder, onChange, anonymous, short }: {
  internal: string
  responder: string
  onChange: (kind: NoteKind, text: string) => void
  anonymous: boolean
  short?: boolean
}) {
  return (
    <div className="cn-pair">
      {(['internal', 'responder'] as const).map((kind) => {
        const value = kind === 'internal' ? internal : responder
        return (
          <div className={`cn cn-${kind}`} key={kind}>
            <Head kind={kind} short={short} />
            {/* AN ANONYMOUS CALLER'S NAME NEVER GOES OUT (061) -- but this
                box sends what is typed in it, so it says so. */}
            {kind === 'responder' && anonymous && (
              <div className="cn-warn">The caller is anonymous: don't write his name or number here.</div>
            )}
            <GrowingText value={value} maxLength={NOTE_MAX} aria-label={WORDS[kind].title}
                         onChange={(e) => onChange(kind, e.target.value)} />
            <Room length={value.length} />
          </div>
        )
      })}
    </div>
  )
}

/** ONE NOTE IN CORRECT THIS CALL: its own colours, who wrote it and when,
 *  and "changed" once it differs. The server keeps what it said. */
export function NoteEditor({ note, value, onChange }: {
  note: CallNote; value: string; onChange: (text: string) => void
}) {
  const changed = value.trim() !== note.body.trim()
  return (
    <div className={`cn cn-${note.kind}`}>
      <Head kind={note.kind} short />
      <small className="cn-who">
        {note.written_by_label} · {hhmm(note.written_at)}
        {note.at_intake ? ', when the call was taken' : ''}
        {changed && <> <span className="pill ccor-ed">changed</span></>}
      </small>
      <GrowingText value={value} maxLength={NOTE_MAX} aria-label={WORDS[note.kind].title}
                   onChange={(e) => onChange(e.target.value)} />
      <Room length={value.length} />
    </div>
  )
}

/** A MEMBER GOING, for the ticks (082): who, and whether a text or an email
 *  can reach him at all -- a radio-only member is told on the air. */
export type GoingMember = { member_id: string; who: string; reachable?: boolean }

/** Up to this many going, the ticks sit under the box; more, a pop-up. */
const INLINE_MAX = 6

/** "Added and sent to MT-51 by text and email · 14:11." -- who, how, when,
 *  the way Resend already says it; amber when somebody could not be told. */
function said(out: NoteSent | undefined, chose: number, added = true): { ok: boolean; text: string } | null {
  if (!out) return chose ? null : { ok: true, text: 'Added. Sent to nobody.' }
  if (!out.sent) return { ok: false, text: `Added, but not sent: ${out.detail ?? 'unknown reason'}` }
  const when = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  const how = out.by && out.by.length ? ` by ${out.by.join(' and ')}` : ''
  const radio = out.radio_only && out.radio_only.length
    ? ` ${out.radio_only.join(', ')} has no text or email set up — tell them by radio.` : ''
  return { ok: !radio, text: `${added ? 'Added and sent' : 'Sent'} to ${(out.to ?? []).join(', ')}${how} · ${when}.${radio}` }
}

/** WHOM A NOTE FOR THE RESPONDER HAS REACHED, and who going has not had it
 *  -- with "send it to him", which sends this line only (082). */
function Reach({ note, onSend, sending, readOnly }: {
  note: CallNote; onSend?: (note: CallNote) => void; sending?: boolean; readOnly?: boolean
}) {
  const sent = note.sent_to ?? []
  const waiting = note.not_yet ?? []
  const going = note.sending ?? []
  return (
    <>
      {sent.length > 0 && (
        <> · sent to {sent.map((s) => `${s.who} at ${hhmm(s.at)}`).join(', ')}</>
      )}
      {going.length > 0 && <> · being sent to {going.join(', ')}</>}
      {waiting.length > 0 && (
        <> · <b className="cn-notyet">not sent yet to {waiting.join(', ')}</b>
          {onSend && (
            <>{' — '}
              <button type="button" className="cn-resend" disabled={sending} onClick={() => onSend(note)}>
                {sending ? 'Sending…' : waiting.length > 1 ? 'send it to them' : 'send it to him'}
              </button>
            </>
          )}
        </>
      )}
      {(note.radio_only ?? []).length > 0 && (
        <> · <b className="cn-notyet">radio only: {(note.radio_only ?? []).join(', ')}</b> — tell him on the air</>
      )}
      {sent.length === 0 && waiting.length === 0 && going.length === 0
        && (note.radio_only ?? []).length === 0 && (
        readOnly ? <> · not sent to anybody</> : <> · goes to whoever takes the call</>
      )}
    </>
  )
}

/** THE POP-UP CHECKLIST, for more than six going (the customer: "a small
 *  pop-up and with a checklist"). On the body, never inside the call's
 *  columns: they are CSS containers, and a fixed box inside one is placed by
 *  it. Every change is kept as it is made; Done, Escape or the backdrop close
 *  it, and the button behind still says how many it will send to. */
function PickWho({ people, off, onToggle, onAll, onNone, onClose }: {
  people: GoingMember[]
  off: Set<string>
  onToggle: (id: string) => void
  onAll: () => void
  onNone: () => void
  onClose: () => void
}) {
  const [find, setFind] = useState('')
  const pressed = useRef(false)
  // ESCAPE CLOSES IT WHEREVER THE FOCUS WENT -- a click on its padding moves
  // focus to the page -- and only it: stopped here, so nothing behind it
  // also takes the key.
  useEffect(() => {
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', esc, true)
    return () => window.removeEventListener('keydown', esc, true)
  }, [onClose])
  const sendable = people.filter((p) => p.reachable !== false)
  const ticked = sendable.filter((p) => !off.has(p.member_id)).length
  const shown = people.filter((p) => p.who.toLowerCase().includes(find.trim().toLowerCase()))
  return createPortal(
    <div className="modalback"
         onMouseDown={(e) => { pressed.current = e.target === e.currentTarget }}
         onMouseUp={(e) => { if (pressed.current && e.target === e.currentTarget) onClose() }}>
      <div className="modal cn-pick" role="dialog" aria-modal="true" aria-label="Send it to">
        <div className="cn-pick-h"><h3>Send it to</h3><span>{ticked} of {people.length} ticked</span></div>
        <input type="text" value={find} autoFocus placeholder="Find a unit or a name…"
               aria-label="Find a unit or a name" onChange={(e) => setFind(e.target.value)} />
        <div className="cn-pick-acts">
          <button type="button" onClick={onAll}>All</button>
          <button type="button" onClick={onNone}>None</button>
        </div>
        <div className="cn-pick-list">
          {shown.map((p) => (
            <label key={p.member_id} className={p.reachable === false ? 'dim' : ''}>
              <input type="checkbox" disabled={p.reachable === false}
                     checked={p.reachable !== false && !off.has(p.member_id)}
                     onChange={() => onToggle(p.member_id)} />
              {p.who}{p.reachable === false && ' · radio only'}
            </label>
          ))}
          {shown.length === 0 && <div className="dim">Nobody going by that name.</div>}
        </div>
        <div className="modalrow">
          <button type="button" className="btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body)
}

/** THE OPEN CALL'S NOTES (his Q1 A): every one with who and when, and a box
 *  to add more. A NOTE FOR THE RESPONDER GOES OUT WHEN IT IS ADDED (082) to
 *  the members going -- every one ticked by default, unticked as wanted --
 *  as its own short message. Read-only on Past calls and on a call closed a
 *  moment ago. Keyed on the call by its caller, so a half-typed addition never
 *  follows the dispatcher to the next call. */
export function CallNotes({ callId, notes, readOnly, anonymous, onAdded, going = [], canSend }: {
  callId: string
  notes: CallNote[]
  readOnly?: boolean
  anonymous?: boolean
  onAdded?: () => Promise<unknown> | void
  /** The members going, for the ticks (082). */
  going?: GoingMember[]
  /** The role may send to members (the broadcast leaf, as Resend). */
  canSend?: boolean
}) {
  const [typing, setTyping] = useState<Record<NoteKind, string>>({ internal: '', responder: '' })
  /** What the boxes hold NOW, for a reply arriving late. */
  const typingNow = useRef(typing)
  typingNow.current = typing
  const [busy, setBusy] = useState<NoteKind | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)
  /** Who has been UNTICKED -- so anybody logged as going while the box is
   *  being typed in starts ticked, like everybody else. */
  const [off, setOff] = useState<Set<string>>(new Set())
  const [picking, setPicking] = useState(false)
  const chooser = useRef<HTMLButtonElement>(null)
  const [sendingNote, setSendingNote] = useState<number | null>(null)
  useEffect(() => {
    setTyping({ internal: '', responder: '' }); setError(null); setResult(null)
    setOff(new Set()); setPicking(false)
  }, [callId])

  const people = going.filter((g, i) => going.findIndex((o) => o.member_id === g.member_id) === i)
  const chosen = people.filter((p) => p.reachable !== false && !off.has(p.member_id))
  const sends = !!canSend && people.length > 0

  const toggle = (id: string) => setOff((o) => {
    const next = new Set(o)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  async function add(kind: NoteKind) {
    const text = typing[kind]
    if (!text.trim() || busy) return
    const to = kind === 'responder' && sends ? chosen.map((p) => p.member_id) : []
    setBusy(kind)
    setError(null)
    setResult(null)
    try {
      const out = await api.incidentCallNote(callId, kind, text, to)
      // EMPTIED ONLY IF IT STILL HOLDS WHAT WAS SENT: words typed while it
      // said "Adding…" are the next note, not typing to throw away.
      setTyping((t) => (t[kind] === text ? { ...t, [kind]: '' } : t))
      if (kind === 'responder' && sends) {
        setResult(said(out.sent, to.length))
        // THE TICKS START AGAIN ONLY WITH AN EMPTY BOX: an untick made for the
        // next note, typed while this one was adding, is kept.
        if (typingNow.current[kind] === text) setOff(new Set())
      }
      await onAdded?.()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The note could not be added. It is still in the box.')
    } finally {
      setBusy(null)
    }
  }

  /** "Send it to him": this note only, to whoever going has not had it. */
  async function sendNote(note: CallNote) {
    const ids = note.not_yet_ids ?? []
    if (!ids.length || sendingNote !== null) return
    setSendingNote(note.id)
    setError(null)
    setResult(null)
    try {
      setResult(said(await api.incidentCallNoteSend(callId, note.id, ids), ids.length, false))
      await onAdded?.()
    } catch (e) {
      setResult({ ok: false, text: e instanceof ApiError ? e.message : 'The note could not be sent.' })
    } finally {
      setSendingNote(null)
    }
  }

  if (readOnly && notes.length === 0) return null
  return (
    <div className="cn-call">
      <div className="cn-pair">
        {(['internal', 'responder'] as const).map((kind) => {
          const mine = notes.filter((n) => n.kind === kind)
          return (
            <div className={`cn cn-${kind}`} key={kind}>
              <Head kind={kind} short />
              {mine.length === 0 && <div className="cn-none">None yet.</div>}
              {mine.map((n) => (
                <div className="cn-entry" key={n.id}>
                  <div className="cn-body">{n.body}</div>
                  <small>
                    {n.written_by_label} · {hhmm(n.written_at)}
                    {n.at_intake ? ', when the call was taken' : ''}
                    {kind === 'responder' && (
                      <Reach note={n} onSend={readOnly || !canSend ? undefined : sendNote}
                             sending={sendingNote === n.id} readOnly={readOnly} />
                    )}
                  </small>
                </div>
              ))}
              {kind === 'responder' && result && (
                <div className={`cn-said ${result.ok ? 'ok' : 'warn'}`} role="status">{result.text}</div>
              )}
              {!readOnly && (
                <div className="cn-add">
                  {kind === 'responder' && anonymous && (
                    <div className="cn-warn">The caller is anonymous: don't write his name or number here.</div>
                  )}
                  <GrowingText
                    value={typing[kind]} maxLength={NOTE_MAX} rows={2}
                    placeholder={kind === 'internal' ? 'Add to the internal notes…' : 'Add for the responder…'}
                    aria-label={kind === 'internal' ? 'Add to the internal notes' : 'Add a note for the responder'}
                    onChange={(e) => setTyping({ ...typing, [kind]: e.target.value })}
                    onKeyDown={(e) => {
                      // CTRL+ENTER ADDS (and sends, as the button says); Enter
                      // alone is a new line -- a caller's story has paragraphs.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
                        e.preventDefault()
                        add(kind)
                      }
                    }}
                  />
                  <Room length={typing[kind].length} />
                  {typing[kind].trim() && (
                    <>
                      {/* WHO IT GOES TO NOW (082): ticks under the box for up
                          to six going, a line and a pop-up for more. */}
                      {kind === 'responder' && sends && people.length <= INLINE_MAX && (
                        <div className="cn-to">
                          <span className="lab">Send it now to:</span>
                          {people.map((p) => (
                            <label key={p.member_id} className={p.reachable === false ? 'dim' : ''}>
                              <input type="checkbox" disabled={p.reachable === false}
                                     checked={p.reachable !== false && !off.has(p.member_id)}
                                     onChange={() => toggle(p.member_id)} />
                              {p.who}{p.reachable === false && ' · radio only, tell him on the air'}
                            </label>
                          ))}
                        </div>
                      )}
                      {kind === 'responder' && sends && people.length > INLINE_MAX && (
                        <div className="cn-to">
                          <span className="lab">Send it now to:</span>
                          <b>{chosen.length} of {people.length} going</b>
                          <button type="button" className="cn-resend" ref={chooser}
                                  onClick={() => setPicking(true)}>choose…</button>
                        </div>
                      )}
                      {kind === 'responder' && !readOnly && people.length === 0 && (
                        <div className="cn-hint">Nobody is going yet. It goes to whoever takes the call, with the full address.</div>
                      )}
                      {kind === 'responder' && people.length > 0 && !canSend && (
                        <div className="cn-hint">Your role can't send messages to members. The note is kept and goes to whoever is logged as going next.</div>
                      )}
                      <button type="button" className="btn sm cn-addbtn" disabled={busy !== null}
                              onClick={() => add(kind)}>
                        {busy === kind ? 'Adding…'
                          : kind === 'responder' && sends
                            ? chosen.length ? `Add & send to ${chosen.length}` : 'Add, send to nobody'
                            : 'Add note'}
                      </button>
                    </>
                  )}
                  {kind === 'responder' && picking && (
                    <PickWho people={people} off={off} onToggle={toggle}
                             onAll={() => setOff(new Set())}
                             onNone={() => setOff(new Set(people.map((p) => p.member_id)))}
                             onClose={() => { setPicking(false); chooser.current?.focus() }} />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {error && <p className="cn-error" role="alert">{error}</p>}
    </div>
  )
}
